import {
  ApiSimpleSubscription,
  ApiSubscriptionMerchant,
  ApiSubscriptionStatus,
} from "@incendium/api";
import {
  Button,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import Loading from "Components/Loading/Loading";
import SpacedListContainer from "Components/UI/SpacedListContainer";
import {
  activateSubscription,
  deleteSubscription,
  saveSubcription,
  SubscriptionCard,
  SubscriptionDrawer,
} from "features/subscription";
import { useSubscriptions } from "features/subscription/";
import { useNotification } from "Hooks";
import { useConfirmation } from "Hooks/useConfirmation";
import { useSave } from "Hooks/useSave";
import produce from "immer";
import { useCallback, useMemo, useState } from "react";

type MerchantFilter = "both" | ApiSubscriptionMerchant;
type TriFilter = "all" | "yes" | "no";

function SubscriptionPages() {
  const { subscriptions, setSubscriptions, loading } = useSubscriptions();
  const [open, setOpen] = useState(false);
  const [subscription, setSubscription] = useState<ApiSimpleSubscription>({});
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const { saving, save } = useSave();
  const handleConfirmation = useConfirmation();
  const [openProjects, setOpenProjects] =
    useState<ApiSimpleSubscription | null>(null);

  const [merchantFilter, setMerchantFilter] = useState<MerchantFilter>("both");
  const [publicFilter, setPublicFilter] = useState<TriFilter>("all");
  const [activeFilter, setActiveFilter] = useState<TriFilter>("all");

  const filteredSubscriptions = useMemo(() => {
    return subscriptions.filter((sub) => {
      if (
        merchantFilter !== "both" &&
        (sub.merchant ||
          ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_STRIPE) !==
          merchantFilter
      ) {
        return false;
      }

      if (publicFilter === "yes" && !sub._public) {
        return false;
      }
      if (publicFilter === "no" && sub._public) {
        return false;
      }

      const isActive =
        sub.status === ApiSubscriptionStatus.SUBSCRIPTION_STATUS_ACTIVE;
      if (activeFilter === "yes" && !isActive) {
        return false;
      }
      if (activeFilter === "no" && isActive) {
        return false;
      }

      return true;
    });
  }, [subscriptions, merchantFilter, publicFilter, activeFilter]);

  const onEdit = useCallback((subscription: ApiSimpleSubscription) => {
    setSubscription(subscription);
    setOpen(true);
  }, []);

  const onDelete = useCallback(
    (subscription: ApiSimpleSubscription) => {
      handleConfirmation({
        title: `Are you sure you want to delete this subscription`,
        body: `This action cannot be undone.`,
        callback: async () => {
          await deleteSubscription(subscription.id || "");
          setSubscriptions((subs) =>
            produce(subs, (draft) => {
              const idx = draft.findIndex((d) => d.id === subscription.id);
              if (idx >= 0) {
                draft.splice(idx, 1);
              }
            }),
          );
          return "Subscription Deleted";
        },
      });
    },
    [handleConfirmation, setSubscriptions],
  );

  const onSave = useCallback(() => {
    try {
      save(async () => {
        const res = await saveSubcription(subscription);
        setSubscription({});
        setSubscriptions((subs) =>
          produce(subs, (draft) => {
            const idx = draft.findIndex((d) => d.id === res.id);
            if (idx >= 0) {
              draft[idx] = res;
            } else {
              draft.push(res);
            }
          }),
        );
        setOpen(false);
        showSuccessNotification("Subscription Saved");
      });
    } catch (error) {
      showErrorNotification("Failed to save subscription, please try again");
    }
  }, [
    subscription,
    save,
    setSubscriptions,
    setSubscription,
    showErrorNotification,
    showSuccessNotification,
  ]);

  const onActivate = useCallback(
    (activate: boolean, sub: ApiSimpleSubscription) => {
      if (activate) {
        handleConfirmation({
          title: `Are you sure you want to activate this subscription`,
          body: `Once this is done you will no longer be able to edit or delete this subscription`,
          callback: async () => {
            const res = await activateSubscription(sub.id || "");
            setSubscriptions((subs) =>
              produce(subs, (draft) => {
                const idx = draft.findIndex((d) => d.id === sub.id);
                if (idx >= 0) {
                  draft[idx] = res;
                }
              }),
            );
            return "Subscription Activated";
          },
        });
      }
    },
    [handleConfirmation, setSubscriptions],
  );
  const onMakePublic = useCallback(
    (_public: boolean, sub: ApiSimpleSubscription) => {
      handleConfirmation({
        title: `Are you sure you want to make this subscription ${
          _public ? "Public" : "Un Public"
        }`,
        body: _public
          ? `This will mean the subscription will be selectable from a users account page`
          : `This will mean the subscription is not viewable from the accounts page`,
        callback: async () => {
          const res = await saveSubcription({ ...sub, _public });
          setSubscriptions((subs) =>
            produce(subs, (draft) => {
              const idx = draft.findIndex((d) => d.id === sub.id);
              if (idx >= 0) {
                draft[idx] = res;
              }
            }),
          );
          return "Subscription Public status changed";
        },
      });
    },
    [handleConfirmation, setSubscriptions],
  );

  const onShowProjects = useCallback((subscription: ApiSimpleSubscription) => {
    setOpenProjects(subscription);
  }, []);

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (loading) {
    return <Loading fullHeight />;
  }
  return (
    <>
      <SpacedListContainer
        title={"Manager Custom Subscriptions"}
        action={<Button onClick={() => setOpen(true)}>New Subscription</Button>}
      >
        <Stack
          direction="row"
          alignItems="center"
          flexWrap="wrap"
          gap={3}
          mb={3}
        >
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Merchant
            </Typography>
            <ToggleButtonGroup
              size="small"
              color="secondary"
              exclusive
              value={merchantFilter}
              onChange={(_, value: MerchantFilter | null) => {
                if (value) {
                  setMerchantFilter(value);
                }
              }}
            >
              <ToggleButton value="both">Both</ToggleButton>
              <ToggleButton
                value={ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_STRIPE}
              >
                Stripe
              </ToggleButton>
              <ToggleButton
                value={ApiSubscriptionMerchant.SUBSCRIPTION_MERCHANT_SHOPIFY}
              >
                Shopify
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Public
            </Typography>
            <ToggleButtonGroup
              size="small"
              color="secondary"
              exclusive
              value={publicFilter}
              onChange={(_, value: TriFilter | null) => {
                if (value) {
                  setPublicFilter(value);
                }
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="yes">Public</ToggleButton>
              <ToggleButton value="no">Private</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">
              Active
            </Typography>
            <ToggleButtonGroup
              size="small"
              color="secondary"
              exclusive
              value={activeFilter}
              onChange={(_, value: TriFilter | null) => {
                if (value) {
                  setActiveFilter(value);
                }
              }}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="yes">Active</ToggleButton>
              <ToggleButton value="no">Inactive</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>

        <Grid container spacing={2}>
          {filteredSubscriptions.map((subscription) => (
            <Grid key={subscription.id} item xs={3}>
              <SubscriptionCard
                subscription={subscription}
                onEdit={onEdit}
                onDelete={onDelete}
                onActivate={onActivate}
                onMakePublic={onMakePublic}
                onShowProjects={onShowProjects}
              />
            </Grid>
          ))}
        </Grid>
      </SpacedListContainer>

      <SubscriptionDrawer
        open={open}
        onClose={onClose}
        subscription={subscription}
        setSubscription={setSubscription}
        onSubmit={onSave}
        saving={saving}
      />
      <CustomDialog
        fullWidth
        maxWidth="md"
        open={openProjects !== null}
        onClose={() => setOpenProjects(null)}
      >
        <DialogTitle>
          Projects Subscribed to {openProjects?.product?.name}
        </DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={150}>Project Internal ID</TableCell>
                  <TableCell>Project Name</TableCell>
                  <TableCell>Project Environment</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {openProjects?.subscribed?.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>{project.id}</TableCell>
                    <TableCell>{project.name}</TableCell>
                    <TableCell>{project.env}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </CustomDialog>
    </>
  );
}

export default SubscriptionPages;
